import { useState, FunctionComponent, useEffect } from 'react';
import CertificatesApi from '../../api/certificates';
import {
    License,
    CertificateAircraftCategory,
    CertificateRating,
    MxiLicenseCode,
    Skill,
    UpdateLicense,
    SelectObject,
} from '../../types/certificates';
import { Typography, Chip, Input, Snackbar, Dialog, Select, SelectMultiple, Table, TableCell, TableRow } from '../../tailwindcomponents';
import { AddCircle, Edit } from '../../components/icons';
import { Button, IconButton } from '../../tailwindcomponents';
import { TableColumns } from '../../types/table';
import { palette } from '../../theme/palette';

type LicenseProps = {
    setLoading: Function;
    isAdmin: boolean;
};

const sortTableColumns = [
    { label: 'Rating', key: 'rating_name' },
    { label: 'Category', key: 'category_name' },
    { label: 'Code/Description', key: 'license_code' },
    // Skills is a list of chips, so there is no single value to order by - the comparator
    // can't compare arrays and always tied here, making the header a guaranteed no-op.
    // Sorting by a hidden count would order by something the cell doesn't show.
    { label: 'Skills', key: 'skills', sortable: false },
    { label: '', key: '', sortable: false },
] as TableColumns[];

const LicenseTable: FunctionComponent<LicenseProps> = ({ setLoading, isAdmin }) => {

    // Table data
    const [licenses, setLicenses] = useState<License[]>([]);

    // Dropdown options
    const [ratingOptions, setRatingOptions] = useState<SelectObject[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<SelectObject[]>([]);
    const [mxiLicenceCodeOptions, setMxiLicenceCodeOptions] = useState<SelectObject[]>([]);
    const [mxiLicenseCodes, setMxiLicenseCodes] = useState<MxiLicenseCode[]>([]);
    const [maintenerSkills, setMaintenerSkills] = useState<Skill[]>([]);

    // Currently edited / created license
    const [selectedLicense, setSelectedLicense] = useState<UpdateLicense | null>(null);

    // Dialog & UI state
    const [licenseDialog, setLicenseDialog] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'warning' | 'error'>('success');
    const [deleteLicenseDialog, setDeleteLicenseDialog] = useState<boolean>(false);

    useEffect(() => {
        refresh();
    }, []);

    const clearState = () => {
        setSelectedLicense(null);
        setLicenseDialog(false);
    };

    const refresh = () => {
        setLoading(true);
        setLicenses([]);
        setRatingOptions([]);
        setCategoryOptions([]);
        clearState();

        // 1) Categories
        CertificatesApi.GetAllCertififcateAircraftCategories().then(resCat => {
            const allCategories: SelectObject[] = resCat.data.map((item: CertificateAircraftCategory) => ({
                name: item.aircraft_category,
                id: item.id!,
            }));
            const visibleCategories = resCat.data.filter((item: CertificateAircraftCategory) => item.visible === true);
            setCategoryOptions(
                visibleCategories.map((item: CertificateAircraftCategory) => ({
                    name: item.aircraft_category,
                    id: item.id!,
                }))
            );

            // 2) Ratings
            CertificatesApi.GetAllCertificationRatings().then(resRat => {
                const allRatings: SelectObject[] = resRat.data.map((item: CertificateRating) => ({
                    name: item.rating,
                    id: item.id!,
                }));
                const visibleRatings = resRat.data.filter((item: CertificateRating) => item.visible === true);
                setRatingOptions(
                    visibleRatings.map((item: CertificateRating) => ({
                        name: item.rating,
                        id: item.id!,
                    }))
                );

                // 3) Licenses (with rating/category names + skills)
                CertificatesApi.GetAllLicenses().then((res: any) => {
                    const licensesUpdated: License[] = res.data.map((item: License) => ({
                        ...item, // keeps rating_id, category_id, skills, etc.
                        rating_name: allRatings.find(r => r.id === item.rating_id)?.name || '',
                        category_name: allCategories.find(c => c.id === item.category_id)?.name || '',
                        skills: item.skills ?? [],
                    }));
                    const usedLicenseCodes = res.data.map((item: License) => item.license_code);
                    setLicenses(licensesUpdated);

                    // 4) Maintener skills (for assigning skills to license)
                    CertificatesApi.GetMaintenerSkills().then(resSkills => {
                        if (resSkills.status === true) {
                            setMaintenerSkills(resSkills.data);
                        } else {
                            setMaintenerSkills([]);
                            setSnackbarMessage('Failed to fetch Maintener skills');
                            setSnackbarSeverity('error');
                        }
                        setLoading(false);
                    });
                    CertificatesApi.GetAllMxiLicenseCodes().then(res => {
                        const codes = res.data ?? [];

                        if (res.status !== true) {
                            setMxiLicenseCodes([]);
                            setMxiLicenceCodeOptions([]);
                            setSnackbarMessage('Failed to fetch MXI license codes');
                            setSnackbarSeverity('error');
                            return;
                        }

                        setMxiLicenseCodes(codes);
                        const mxiLicenseCodes: SelectObject[] = codes.map((code: MxiLicenseCode, idx: number) => ({
                            name: code.mxiLicCode,
                            id: idx + 1, // artificial id; we only care about name
                        }));
                        const mxiCodes = mxiLicenseCodes.map(c => c.name);
                        const unusedMxiCodes: SelectObject[]  = mxiCodes.filter(v => !usedLicenseCodes.includes(v)).map((c, idx) => ({name: c, id: idx + 1})); // MXI codes that are not yet used in licenses
                        setMxiLicenceCodeOptions(unusedMxiCodes);
                    });
                });
            });
        });
    };

    const openNewLicenseDialog = () => {
        const newLicense: UpdateLicense = {
            id: -1,
            ratingId: -1,
            categoryId: -1,
            license_code: '',
            license_description: '',
            skillIds: [],
        };
        setSelectedLicense(newLicense);
        setLicenseDialog(true);
    };

    const onEditRow = (row: License) => {
        const existingLicense: UpdateLicense = {
            id: row.id,
            ratingId: row.rating_id,
            categoryId: row.category_id,
            license_code: row.license_code,
            license_description: row.license_description ?? '',
            skillIds: row.skills ? row.skills.map(s => s.skill_id) : [],
        };
        setSelectedLicense(existingLicense);
        setLicenseDialog(true);
    };

    const deleteLicense = () => {
        if (!selectedLicense || selectedLicense.id === -1) return;

        setLoading(true);
        CertificatesApi.DeleteLicense(selectedLicense.id).then(res => {
            if (res.status === false) {
                setSnackbarMessage('Failed to delete license');
                setSnackbarSeverity('error');
                setLoading(false);
            } else {
                setSnackbarMessage('Successfully deleted license');
                setSnackbarSeverity('success');
                refresh();
            }
        });
    };

    const AddSkillsToLicense = (licenseId: number, skillIds: number[]) => {
        if (!skillIds) return;

        CertificatesApi.AddSkillsToLicense(licenseId, skillIds).then(res => {
            if (res.status === false) {
                setSnackbarMessage('Failed to assign skills to license');
                setSnackbarSeverity('error');
            } else {
                setSnackbarMessage('Successfully assigned skills to license');
                setSnackbarSeverity('success');
            }
        });
    };

    const onCloseDialog = (status: boolean) => {
        if (status === false) {
            clearState();
            return;
        }

        if (!selectedLicense) {
            setSnackbarMessage('No license selected');
            setSnackbarSeverity('error');
            return;
        }

        const { ratingId, categoryId, license_code } = selectedLicense;

        if (ratingId === -1 || categoryId === -1 || !license_code) {
            setSnackbarMessage('All fields must be selected');
            setSnackbarSeverity('warning');
            return;
        }

        setLicenseDialog(false);
        setLoading(true);

        if (selectedLicense.id === -1) {
            const selectedMxiLicense = mxiLicenseCodes.find(code => code.mxiLicCode === selectedLicense.license_code);
            const licenseExpInterval = selectedMxiLicense?.expInterval;

            if (licenseExpInterval === undefined || licenseExpInterval === null) {
                setSnackbarMessage('No MXI expiration interval found for selected license code');
                setSnackbarSeverity('warning');
                setLoading(false);
                setLicenseDialog(true);
                return;
            }

            CertificatesApi.CreateLicense(
                ratingId,
                categoryId,
                license_code,
                selectedLicense.license_description ?? '',
                licenseExpInterval
            ).then(res => {
                if (res.status === false) {
                    if(res.message.includes("already exists")) {
                        setSnackbarMessage('License already exists');
                    }
                    else{
                        setSnackbarMessage(res.message || 'Failed to create license');
                    }
                    setSnackbarSeverity('error');
                    setLoading(false);
                    clearState();
                } else {
                    const newLicenseId = res.id ?? res.id;
                    if (selectedLicense.skillIds.length > 0) {
                        AddSkillsToLicense(newLicenseId, selectedLicense.skillIds);
                    }
                    setSnackbarMessage('Successfully created license');
                    setSnackbarSeverity('success');
                    setLoading(false);
                    refresh();
                }
            });
        } else {
            const updatedLicense: License = {
                id: selectedLicense.id,
                rating_id: selectedLicense.ratingId,
                category_id: selectedLicense.categoryId,
                license_code: selectedLicense.license_code,
                license_description: selectedLicense.license_description,
            };
            CertificatesApi.UpdateLicense(updatedLicense).then((res: any) => {
                if (res.status === true) {
                    AddSkillsToLicense(selectedLicense.id, selectedLicense.skillIds);
                    setSnackbarMessage('Successfully updated license');
                    setSnackbarSeverity('success');
                    refresh();
                } else {
                    setSnackbarMessage('Failed to update license');
                    setSnackbarSeverity('error');
                    setLoading(false);
                }
            });
        }
    };

    const updateSelectedRating = (ratingId: number) => {
        const foundRating = ratingOptions.find(option => option.id === ratingId);
        if (!foundRating) return;
        setSelectedLicense(prev => (prev ? { ...prev, ratingId: foundRating.id } : prev));
    };

    const updateSelectedCategory = (categoryId: number) => {
        const foundCategory = categoryOptions.find(option => option.id === categoryId);
        if (!foundCategory) return;
        setSelectedLicense(prev => (prev ? { ...prev, categoryId: foundCategory.id } : prev));
    };

    const updateSelectedMxiCode = (mxiCodeId: number) => {
        const foundMxiCode = mxiLicenceCodeOptions.find(option => option.id === mxiCodeId);
        if (!foundMxiCode) return;
        setSelectedLicense(prev => (prev ? { ...prev, license_code: foundMxiCode.name } : prev));
    };

    const updateSelectedLicenseDescription = (description: string) => {
        setSelectedLicense(prev =>
            prev
                ? {
                      ...prev,
                      license_description: description,
                  }
                : prev
        );
    };

    const updateSelectedLicenseSkills = (value: any) => {
        let selectedCodes: string[] = [];

        if (Array.isArray(value)) {
            selectedCodes = value;
        } else if (value && Array.isArray(value.target?.value)) {
            selectedCodes = value.target.value;
        }

        const selectedIds = maintenerSkills
            .filter(s => selectedCodes.includes(s.skill_code))
            .map(s => s.id);

        setSelectedLicense(prev =>
            prev
                ? {
                      ...prev,
                      skillIds: selectedIds,
                  }
                : prev
        );
    };

    // Derive selected IDs for the dialog selects from `selectedLicense`
    const selectedRatingId = selectedLicense?.ratingId ?? -1;
    const selectedCategoryId = selectedLicense?.categoryId ?? -1;
    const selectedMxiCodeId =
        selectedLicense?.license_code
            ? mxiLicenceCodeOptions.find(c => c.name === selectedLicense.license_code)?.id ?? -1
            : -1;

    const isEditing = !!selectedLicense && selectedLicense.id !== -1;

    const generateTableBody = (row: License, index: number) => {
        return (
            <TableRow key={row.id}>
                <TableCell>{index}</TableCell>
                <TableCell>{row.rating_name}</TableCell>
                <TableCell>{row.category_name}</TableCell>
                <TableCell>
                    <div className="flex flex-col">
                        <Typography variant="body1" text={row.license_code} />
                        {row.license_description && (
                              <Typography variant="body1" text={row.license_description} />
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    {row.skills?.map(skill => {
                        const fullSkill = maintenerSkills.find(s => s.id === skill.skill_id);
                        const label = fullSkill ? fullSkill.skill_code : `ID ${skill.skill_id}`;
                        return (
                            <Chip
                                key={skill.id}
                                label={label}
                                size="small"
                                className="mr-1 mb-1"
                            />
                        );
                    })}
                </TableCell>

                {isAdmin ? (
                    <TableCell>
                        <IconButton onClick={() => onEditRow(row)} color="default">
                            <Edit />
                        </IconButton>
                    </TableCell>
                ) : (
                    <TableCell></TableCell>
                )}
            </TableRow>
        );
    };

    return (
        <div>
            <div>
                <div className="flex flex-col items-center mt-7">
                    {!licenseDialog && isAdmin && (
                        <IconButton onClick={openNewLicenseDialog} style={{ color: palette.success }}>
                            <AddCircle />
                        </IconButton>
                    )}
                </div>
                <Table
                    sortTableColumns={sortTableColumns}
                    rows={licenses}
                    generateBody={(row: License, index: number) => generateTableBody(row, index)}
                    defaultRowsPerPage={25}
                />
            </div>

            <Dialog
                visible={licenseDialog}
                max_width={'md'}
                title={isEditing ? 'Edit license' : 'Create new license'}
                context={
                    <div>
                        {/* License code select */}
                        <Select
                            selectedItem={selectedMxiCodeId}
                            width={200}
                            filter_by_text={'License Codes'}
                            optionsObject={mxiLicenceCodeOptions}
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                updateSelectedMxiCode(Number(e.target.value))
                            }
                        />

                        {/* Rating + Category */}
                        <div
                            className="flex flex-row flex-wrap items-center justify-between mt-2.5"
                        >
                            <Select
                                selectedItem={selectedRatingId}
                                width={200}
                                filter_by_text={'Ratings'}
                                optionsObject={ratingOptions}
                                onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                    updateSelectedRating(Number(e.target.value))
                                }
                            />
                            <Select
                                selectedItem={selectedCategoryId}
                                width={75}
                                widthPercentage={true}
                                filter_by_text={'Categories'}
                                optionsObject={categoryOptions}
                                onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                    updateSelectedCategory(Number(e.target.value))
                                }
                            />
                        </div>

                        {/* Description */}
                        <Input
                            multiline={true}
                            help_text={'License description'}
                            label_text={'License description'}
                            value={selectedLicense?.license_description ?? ''}
                            onTextChange={(text: string) => updateSelectedLicenseDescription(text)}
                            type="text"
                            width={100}
                        />

                        {/* Skills */}
                        <Typography margin={[4, 0, 0, 0]} variant="subtitle1" text="Assign Skills to license" />
                        <SelectMultiple
                            onSelectedItem={updateSelectedLicenseSkills}
                            selectedItemsString={
                                selectedLicense?.skillIds.map(skillId => {
                                    const skill = maintenerSkills.find(s => s.id === skillId);
                                    return skill ? skill.skill_code : '';
                                }) || []
                            }
                            customSelectedText={' Skills selected: '}
                            widthPercentage={true}
                            width={30}
                            filter_by_text={'Skills'}
                            options={maintenerSkills.map(skill => skill.skill_code)}
                        />

                        {/* Delete button only when editing existing license */}
                        {isEditing && (
                            <div className="mt-2.5">
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="medium"
                                    onClick={() => setDeleteLicenseDialog(true)}
                                >
                                    Delete license
                                </Button>
                            </div>
                        )}
                    </div>
                }
                onClose={(status: boolean) => onCloseDialog(status)}
                strict={false}
                show_options={true}
            />
            <Dialog
                visible={deleteLicenseDialog}
                max_width={'sm'}
                title={'Confirm delete'}
                context={
                    <Typography
                        variant="body1"
                        text={`Are you sure you want to delete this license? This action cannot be undone.`}
                    />
                }
                onClose={(status: boolean) => {
                    setDeleteLicenseDialog(false);
                    if (status) {
                        deleteLicense();
                    }
                }}
                strict={false}
                show_options={true}
            />

            <Snackbar
                onSnackBarClose={() => setSnackbarMessage('')}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition="right"
                severity={snackbarSeverity}
            />
        </div>
    );
};

export default LicenseTable;